import React from 'react'
import PropTypes from 'prop-types'
import styled from 'styled-components'
import { Flex, Text, Box, getPaletteColor, Button } from 'pcln-design-system'
import { Dollar } from 'pcln-icons'
import AncillaryList from '../AncillaryList'
import { truncateJetBlueNames } from '../../FareBrandDrawer/getUpsellOptionsBySlice/helpers'

const ModalWrapper = styled(Flex)`
  width: 100%;
  flex-wrap: wrap;
`

const ModalHeader = styled(Text.span)`
  width: 100%;
  font-weight: 500;
  font-size: 16px;
  padding-bottom: 24px;
`

const ListHeader = styled(Flex)`
  flex-direction: column;
  border-bottom: 1px solid ${getPaletteColor('border.base')};
  padding-bottom: 10px;
`

const ModalFooter = styled(Text.span)`
  display: flex;
  align-items: center;
  padding-top: 10px;
  border-top: 1px solid ${getPaletteColor('border.base')};
  width: 100%;
`

const ModalFareBrandElement = styled(Flex)`
  border-right: ${(props) => props.borderRight && `1px solid ${getPaletteColor('border.base')(props)}`};
  flex: 1;
  flex-direction: column;
  flex-wrap: wrap;
  padding-bottom: 10px;
`

const ChooseFareButton = styled(Button)`
  width: 95%;
  font-weight: 600;
  font-size: 16px;
`

const FareBrandHeading = styled(Text)`
  width: 160px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`

const SecondaryLabelBox = styled(Box)`
  height: 19px;
`

const PriceContainer = styled(Box)`
  height: 28px;
`

export default function ModalContent(props) {
  const { fareBrands, callToDetails, baseFareToken, upsellToken } = props

  const MainZone = ModalWrapper

  const handleClick = (token, gaAction, gaLabel) => (event) => {
    event.preventDefault()

    if (event.srcEvent) {
      event.srcEvent.stopPropagation()
    } else {
      event.stopPropagation()
    }

    callToDetails(token, gaAction, gaLabel)
  }

  return (
    <MainZone data-autobot-element='upsell-modal-wrapper'>
      <ModalHeader>What&apos;s included?</ModalHeader>
      {fareBrands.map((fareBrand, index) => {
        const { brandAttributes, price, unavailability } = fareBrand
        const isBaseFare = index === 0
        const isSecondShelf = index === 1
        let name = fareBrand.name
        let fareBrandSecondaryLabel = ''
        let fareBrandSecondaryLabelColor = ''
        let showBorderRight = fareBrands.length > 1

        // Truncate Names for JetBlue
        name = truncateJetBlueNames(name)

        if (isBaseFare) {
          fareBrandSecondaryLabel = 'MOST RESTRICTIVE'
          fareBrandSecondaryLabelColor = 'text.base'
        } else if (isSecondShelf) {
          fareBrandSecondaryLabel = 'LESS RESTRICTIVE'
          fareBrandSecondaryLabelColor = 'secondary.base'
          if (fareBrands.length === 2) {
            showBorderRight = false
          }
        } else {
          showBorderRight = false
        }

        let token = baseFareToken
        const gaAction = 'Branded Fare Shelf Interaction'
        let gaLabel = 'CLICKED_BASE_FARE_WHATS_INCLUDED'
        if (!isBaseFare) {
          token = upsellToken
          gaLabel = 'CLICKED_UPGRADE_WHATS_INCLUDED'
        }

        return (
          <ModalFareBrandElement
            key={token}
            borderRight={showBorderRight}
            data-autobot-element='modal-farebrand-element'
          >
            <ListHeader pl={index !== 0 && '10px'}>
              <SecondaryLabelBox>
                {!unavailability && (
                  <Text.span fontSize={'x-small'} bold color={fareBrandSecondaryLabelColor}>
                    {fareBrandSecondaryLabel}
                  </Text.span>
                )}
              </SecondaryLabelBox>
              <FareBrandHeading fontWeight='bold' fontSize='14px'>
                {name}
              </FareBrandHeading>
              <PriceContainer>
                {unavailability ? (
                  <Text fontSize={0} mt={'10px'} color={'text.light'}>
                    {unavailability}
                  </Text>
                ) : (
                  <React.Fragment>
                    <Text.span bold color={'secondary.base'} fontSize={3}>
                      ${price}
                    </Text.span>
                    <Text.span color={'secondary.base'} fontSize={1}>
                      {' '}
                      /person
                    </Text.span>
                  </React.Fragment>
                )}
              </PriceContainer>
            </ListHeader>
            <Flex flexDirection={'column'} justifyContent={'space-between'} pl={index !== 0 && '10px'}>
              {unavailability ? (
                unavailability === 'Not offered' && (
                  <Text.span mt={2} mr={3} color={'text.light'} fontSize={0}>
                    This airline does not offer this fare class at this time.
                  </Text.span>
                )
              ) : (
                <React.Fragment>
                  <AncillaryList brandAttributes={brandAttributes} />
                  <ChooseFareButton
                    color='secondary'
                    size='medium'
                    width={1}
                    onClick={handleClick({
                      token,
                      cabinNames: [name],
                      gaAction,
                      gaLabel,
                    })}
                  >
                    Choose this Fare
                  </ChooseFareButton>
                </React.Fragment>
              )}
            </Flex>
          </ModalFareBrandElement>
        )
      })}
      <ModalFooter>
        <Dollar size='16' />
        <Text fontSize='11px'>Available for Additional Fee</Text>
      </ModalFooter>
    </MainZone>
  )
}

ModalContent.defaultProps = {
  fareBrands: [],
}

ModalContent.propTypes = {
  baseFareToken: PropTypes.string,
  upsellToken: PropTypes.string,
  callToDetails: PropTypes.func,
  fareBrands: PropTypes.arrayOf(
    PropTypes.shape({
      unavailability: PropTypes.string,
      brandAttributes: PropTypes.arrayOf(
        PropTypes.shape({
          type: PropTypes.string,
          description: PropTypes.string,
          inclusivity: PropTypes.string,
        })
      ),
      name: PropTypes.string,
      token: PropTypes.string,
      price: PropTypes.string,
    })
  ),
}
